/**
 * Document Action page -- public page at /a/:token, the landing for an
 * emailed Approve / Reject link (backend: document-sharing/action-service.js).
 *
 * Two deliberate steps: the link only SHOWS what it will do (GET is
 * read-only, so an email scanner prefetching it can never approve anything);
 * the decision is applied only when the recipient presses the confirm button
 * (POST). The page knows nothing about any document type -- title, summary
 * and data rows come from the backend (the same content as the email), and
 * the button label/tone come from the document type's declared action.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getApiUrl } from '@/lib/api-config';
import { StandardButton } from '@/styles/StandardButtons';
import { AuthBanner } from '@/components/AuthBanner';
import '@/components/Setup.css';

interface ActionSummary {
  doc_label: string | null;
  action_label: string;
  action_tone: 'success' | 'danger' | null;
  summary: {
    title: string | null;
    summary: string | null;
    description: string | null;
    data_blocks: { label: string; value: string }[];
  };
}

type PageState =
  | { status: 'loading' }
  | { status: 'error'; code: string }
  | { status: 'ready'; data: ActionSummary }
  | { status: 'submitting'; data: ActionSummary }
  | { status: 'done'; data: ActionSummary };

const ERROR_COPY: Record<string, string> = {
  not_found: 'This link could not be found. It may be incorrect.',
  used: 'A decision has already been recorded with this link.',
  revoked: 'This link is no longer valid. A newer version may have been sent — please check your most recent email.',
  expired: 'This link has expired. Please ask the sender for a new one.',
  invalid_state: 'This document is no longer waiting for a decision.',
  not_available: 'This feature is temporarily unavailable. Please try again later.',
  network: 'We could not reach the server. Check your connection and try again.',
  internal: 'Something went wrong. Please try again.',
};

async function readError(response: Response): Promise<string> {
  try {
    const body = await response.json();
    return typeof body.error === 'string' ? body.error : 'internal';
  } catch {
    return 'internal';
  }
}

export default function DocumentActionPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(getApiUrl(`api/document-actions/${encodeURIComponent(token || '')}`));
        if (cancelled) return;
        if (!response.ok) {
          setState({ status: 'error', code: await readError(response) });
          return;
        }
        setState({ status: 'ready', data: await response.json() });
      } catch {
        if (!cancelled) setState({ status: 'error', code: 'network' });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const confirm = async () => {
    if (state.status !== 'ready') return;
    const { data } = state;
    setSubmitError(null);
    setState({ status: 'submitting', data });
    try {
      const response = await fetch(getApiUrl(`api/document-actions/${encodeURIComponent(token || '')}/confirm`), { method: 'POST' });
      if (!response.ok) {
        setState({ status: 'error', code: await readError(response) });
        return;
      }
      setState({ status: 'done', data });
    } catch {
      setSubmitError(ERROR_COPY.network);
      setState({ status: 'ready', data });
    }
  };

  const data = state.status === 'ready' || state.status === 'submitting' || state.status === 'done' ? state.data : null;
  const docLabel = data?.doc_label?.toLowerCase() || 'document';

  return (
    <div className="setup-container">
      <div className="setup-content" style={{ maxWidth: '28rem' }}>
        <AuthBanner />

        {state.status === 'loading' && <p className="text-sm text-gray-600">Loading…</p>}

        {state.status === 'error' && (
          <>
            <h1 className="h1">Link unavailable</h1>
            <p className="mt-4 text-sm text-gray-600">{ERROR_COPY[state.code] || ERROR_COPY.internal}</p>
          </>
        )}

        {data && state.status !== 'done' && (
          <>
            <h1 className="h1">{data.action_label} {docLabel}?</h1>
            {data.summary.title && <p className="mt-4 text-sm font-semibold text-gray-900">{data.summary.title}</p>}
            {data.summary.summary && <p className="mt-1 text-sm text-gray-600">{data.summary.summary}</p>}
            {data.summary.data_blocks.length > 0 && (
              <dl className="mt-4 space-y-1">
                {data.summary.data_blocks.map((b) => (
                  <div key={b.label} className="flex gap-3 text-sm">
                    <dt className="text-gray-500">{b.label}</dt>
                    <dd className="font-semibold text-gray-900">{b.value}</dd>
                  </div>
                ))}
              </dl>
            )}
            {data.summary.description && <p className="mt-4 whitespace-pre-wrap text-sm text-gray-600">{data.summary.description}</p>}
            {submitError && <p className="error-message mt-4" role="alert">{submitError}</p>}
            <div className="mt-6 flex justify-end">
              <StandardButton
                customVariant={data.action_tone === 'success' ? 'blue' : 'white'}
                onClick={confirm}
                disabled={state.status === 'submitting'}
              >
                {state.status === 'submitting' ? 'Saving…' : `Confirm: ${data.action_label}`}
              </StandardButton>
            </div>
          </>
        )}

        {state.status === 'done' && data && (
          <>
            <h1 className="h1">Thank you</h1>
            <p className="mt-4 text-sm text-gray-600">
              Your decision ({data.action_label.toLowerCase()}) on this {docLabel} has been recorded and the sender was notified.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
